"use client";

import { c as _c } from "react/compiler-runtime";
import * as React from "react";
import { ChevronDown, ChevronUp } from "lucide-react";
import { Icon } from "../icon";
import { RequiredMark } from "../required-mark";
import { Tag } from "../tag";
import { cn } from "../utils";
import type { MultiSelectProps } from "./multi-select.types";

/** Lists longer than this become type-to-filter by default. */
const SEARCH_THRESHOLD = 8;

/**
 * Multi-Select Component
 *
 * V&A Waterfront Design System - Compact Multi-Select Dropdown
 * Figma: https://www.figma.com/design/CV2ePEn9S0SSZ7T3AAPqF4/V-A-Waterfront---Website---Foundation-Design-System?node-id=11961-20801
 *
 * A compact, closed dropdown that opens on click. Selections render inside the
 * field as removable `Tag` chips and the component emits an array of values.
 * Mirrors the styling and accessibility of `Select`, with multi-selection
 * semantics (the list stays open while choosing). For long lists you can type
 * inline to filter the options.
 */
const MultiSelect = React.forwardRef<HTMLDivElement, MultiSelectProps>((t0, ref) => {
  const $ = _c(140);
  const {
    id: idProp,
    name,
    label,
    placeholder: t1,
    helperText,
    errorText,
    onDark: t2,
    options: t3,
    value,
    onChange,
    onBlur,
    disabled: t4,
    required,
    searchable,
    className
  } = t0;
  const placeholder = t1 === undefined ? "Select..." : t1;
  const onDark = t2 === undefined ? false : t2;
  const options = t3 === undefined ? [] : t3;
  const disabled = t4 === undefined ? false : t4;
  const generatedId = React.useId();
  const id = idProp ?? generatedId;
  const listboxId = `${id}-listbox`;
  const descriptionId = `${id}-description`;
  let t5;
  if ($[0] !== value) {
    t5 = Array.isArray(value) ? value : [];
    $[0] = value;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  const selectedValues = t5;
  const [isOpen, setIsOpen] = React.useState(false);
  const [highlightedIndex, setHighlightedIndex] = React.useState(-1);
  const [query, setQuery] = React.useState("");
  const internalRef = React.useRef(null);
  let t6;
  if ($[2] !== ref) {
    t6 = node => {
      (internalRef as React.MutableRefObject<HTMLDivElement | null>).current = node;
      if (typeof ref === "function") {
        ref(node);
      } else {
        if (ref) {
          (ref as React.MutableRefObject<HTMLDivElement | null>).current = node;
        }
      }
    };
    $[2] = ref;
    $[3] = t6;
  } else {
    t6 = $[3];
  }
  const containerRef = t6;
  const listRef = React.useRef(null);
  const inputRef = React.useRef(null);
  const hasError = !!errorText;
  let t7;
  if ($[4] !== selectedValues) {
    t7 = o => selectedValues.includes(o.value);
    $[4] = selectedValues;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  const selectedOptions = options.filter(t7);
  const isEmpty = selectedOptions.length === 0;
  const filterable = searchable ?? options.length > SEARCH_THRESHOLD;
  const q = query.trim().toLowerCase();
  const filteredOptions = !filterable || q === "" ? options : options.filter(o_0 => o_0.label.toLowerCase().includes(q));
  let t8;
  let t9;
  if ($[6] !== isOpen || $[7] !== onBlur) {
    t8 = () => {
      if (!isOpen) {
        return;
      }
      const handleClick = function handleClick(e) {
        if (internalRef.current && !internalRef.current.contains(e.target as Node)) {
          setIsOpen(false);
          onBlur?.();
        }
      };
      const handleFocusOut = function handleFocusOut(e_0) {
        if (internalRef.current && !internalRef.current.contains(e_0.relatedTarget as Node)) {
          setIsOpen(false);
          onBlur?.();
        }
      };
      document.addEventListener("mousedown", handleClick);
      internalRef.current?.addEventListener("focusout", handleFocusOut);
      const container = internalRef.current;
      return () => {
        document.removeEventListener("mousedown", handleClick);
        container?.removeEventListener("focusout", handleFocusOut);
      };
    };
    t9 = [isOpen, onBlur];
    $[6] = isOpen;
    $[7] = onBlur;
    $[8] = t8;
    $[9] = t9;
  } else {
    t8 = $[8];
    t9 = $[9];
  }
  React.useEffect(t8, t9);
  let t10;
  let t11;
  if ($[10] !== highlightedIndex || $[11] !== isOpen) {
    t10 = () => {
      if (!isOpen || highlightedIndex < 0) {
        return;
      }
      const list = listRef.current;
      const item = list?.children[highlightedIndex] as HTMLElement | undefined;
      item?.scrollIntoView({
        block: "nearest"
      });
    };
    t11 = [highlightedIndex, isOpen];
    $[10] = highlightedIndex;
    $[11] = isOpen;
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  React.useEffect(t10, t11);
  let t12;
  if ($[14] !== filterable || $[15] !== isOpen || $[16] !== setQuery) {
    t12 = () => {
      if (isOpen && filterable) {
        inputRef.current?.focus();
      } else {
        if (!isOpen) {
          setQuery("");
        }
      }
    };
    $[14] = filterable;
    $[15] = isOpen;
    $[16] = setQuery;
    $[17] = t12;
  } else {
    t12 = $[17];
  }
  let t13;
  if ($[18] !== filterable || $[19] !== isOpen) {
    t13 = [isOpen, filterable];
    $[18] = filterable;
    $[19] = isOpen;
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  React.useEffect(t12, t13);
  let t14;
  if ($[21] !== disabled || $[22] !== isOpen || $[23] !== setHighlightedIndex) {
    t14 = function open() {
      if (disabled || isOpen) {
        return;
      }
      setHighlightedIndex(0);
      setIsOpen(true);
    };
    $[21] = disabled;
    $[22] = isOpen;
    $[23] = setHighlightedIndex;
    $[24] = t14;
  } else {
    t14 = $[24];
  }
  const open = t14;
  let t15;
  if ($[25] !== disabled || $[26] !== isOpen || $[27] !== open) {
    t15 = function toggle() {
      if (disabled) {
        return;
      }
      if (isOpen) {
        setIsOpen(false);
      } else {
        open();
      }
    };
    $[25] = disabled;
    $[26] = isOpen;
    $[27] = open;
    $[28] = t15;
  } else {
    t15 = $[28];
  }
  const toggle = t15;
  let t16;
  if ($[29] !== onChange || $[30] !== selectedValues || $[31] !== setQuery) {
    t16 = function toggleOption(optionValue) {
      const next = selectedValues.includes(optionValue) ? selectedValues.filter(v => v !== optionValue) : [...selectedValues, optionValue];
      onChange?.(next);
      setQuery("");
    };
    $[29] = onChange;
    $[30] = selectedValues;
    $[31] = setQuery;
    $[32] = t16;
  } else {
    t16 = $[32];
  }
  const toggleOption = t16;
  let t17;
  if ($[33] !== onChange || $[34] !== selectedValues) {
    t17 = function removeValue(optionValue_0) {
      onChange?.(selectedValues.filter(v_0 => v_0 !== optionValue_0));
    };
    $[33] = onChange;
    $[34] = selectedValues;
    $[35] = t17;
  } else {
    t17 = $[35];
  }
  const removeValue = t17;
  const moveHighlight = function moveHighlight(direction) {
    setHighlightedIndex(i => {
      const n = filteredOptions.length;
      if (n === 0) {
        return -1;
      }
      if (direction === "down") {
        return i < n - 1 ? i + 1 : 0;
      }
      return i > 0 ? i - 1 : n - 1;
    });
  };
  const handleKeyDown = function handleKeyDown(e_1, fromInput) {
    if (disabled) {
      return;
    }
    bb184: switch (e_1.key) {
      case "Enter":
        {
          e_1.preventDefault();
          if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            toggleOption(filteredOptions[highlightedIndex].value);
          } else {
            toggle();
          }
          break bb184;
        }
      case " ":
        {
          if (fromInput && isOpen) {
            break bb184;
          }
          e_1.preventDefault();
          if (isOpen && highlightedIndex >= 0 && filteredOptions[highlightedIndex]) {
            toggleOption(filteredOptions[highlightedIndex].value);
          } else {
            toggle();
          }
          break bb184;
        }
      case "ArrowDown":
        {
          e_1.preventDefault();
          if (!isOpen) {
            open();
          } else {
            moveHighlight("down");
          }
          break bb184;
        }
      case "ArrowUp":
        {
          e_1.preventDefault();
          if (isOpen) {
            moveHighlight("up");
          }
          break bb184;
        }
      case "Backspace":
        {
          if (fromInput && query !== "") {
            break bb184;
          }
          if (!isEmpty) {
            e_1.preventDefault();
            removeValue(selectedValues[selectedValues.length - 1]);
          }
          break bb184;
        }
      case "Escape":
        {
          e_1.preventDefault();
          setIsOpen(false);
        }
    }
  };
  const activeDescendant = isOpen && filteredOptions[highlightedIndex] ? `${id}-option-${highlightedIndex}` : undefined;
  const t18 = "multi-select";
  const t19 = onDark && "dark";
  let t20;
  if ($[36] !== className || $[37] !== t19) {
    t20 = cn(t19, "flex w-full flex-col gap-2", className);
    $[36] = className;
    $[37] = t19;
    $[38] = t20;
  } else {
    t20 = $[38];
  }
  let t21;
  if ($[39] !== disabled || $[40] !== name || $[41] !== selectedValues) {
    t21 = name && selectedValues.map(v_1 => <input key={v_1} type="hidden" name={name} value={v_1} disabled={disabled} />);
    $[39] = disabled;
    $[40] = name;
    $[41] = selectedValues;
    $[42] = t21;
  } else {
    t21 = $[42];
  }
  const t22 = `${id}-label`;
  const t23 = disabled ? "text-input-text-disabled" : "text-input-text-label";
  let t24;
  if ($[43] !== t23) {
    t24 = cn("typography-component-sm-medium desktop:typography-component-md-medium", t23);
    $[43] = t23;
    $[44] = t24;
  } else {
    t24 = $[44];
  }
  let t25;
  if ($[45] !== required) {
    t25 = <RequiredMark required={required} />;
    $[45] = required;
    $[46] = t25;
  } else {
    t25 = $[46];
  }
  let t26;
  if ($[47] !== id || $[48] !== label || $[49] !== t22 || $[50] !== t24 || $[51] !== t25) {
    t26 = <label id={t22} htmlFor={id} className={t24}>{label}{t25}</label>;
    $[47] = id;
    $[48] = label;
    $[49] = t22;
    $[50] = t24;
    $[51] = t25;
    $[52] = t26;
  } else {
    t26 = $[52];
  }
  const t27 = "relative";
  const t28 = filterable ? undefined : id;
  const t29 = filterable ? "group" : "combobox";
  const t30 = filterable ? undefined : isOpen;
  const t31 = filterable ? undefined : "listbox";
  const t32 = filterable ? undefined : listboxId;
  const t33 = `${id}-label`;
  const t34 = filterable ? undefined : activeDescendant;
  const t35 = !filterable && hasError ? true : undefined;
  const t36 = !filterable && (hasError || helperText) ? descriptionId : undefined;
  const t37 = disabled || undefined;
  const t38 = filterable || disabled ? undefined : 0;
  let t39;
  if ($[53] !== disabled || $[54] !== filterable || $[55] !== open || $[56] !== toggle) {
    t39 = () => {
      if (disabled) {
        return;
      }
      if (filterable) {
        inputRef.current?.focus();
        open();
      } else {
        toggle();
      }
    };
    $[53] = disabled;
    $[54] = filterable;
    $[55] = open;
    $[56] = toggle;
    $[57] = t39;
  } else {
    t39 = $[57];
  }
  let t40;
  if ($[58] !== filterable || $[59] !== handleKeyDown) {
    t40 = filterable ? undefined : e_2 => handleKeyDown(e_2, false);
    $[58] = filterable;
    $[59] = handleKeyDown;
    $[60] = t40;
  } else {
    t40 = $[60];
  }
  const t41 = !filterable && "cursor-pointer";
  const t42 = hasError && "bg-input-surface-error hover:bg-input-surface-error focus-within:bg-input-surface-error";
  const t43 = disabled && "pointer-events-none bg-input-surface-disabled text-input-text-disabled cursor-not-allowed";
  let t44;
  if ($[61] !== t41 || $[62] !== t42 || $[63] !== t43) {
    t44 = cn("flex w-full items-center justify-between gap-2 cursor-text", "min-h-12 rounded-sm px-4 py-2", "typography-body-sm-regular", "outline-none", "border border-transparent", "bg-input-surface-rest", "hover:bg-input-surface-hover", "focus-within:border-border-subtle focus-within:bg-input-surface-hover [.dark_&]:focus-within:border-transparent", t41, t42, t43);
    $[61] = t41;
    $[62] = t42;
    $[63] = t43;
    $[64] = t44;
  } else {
    t44 = $[64];
  }
  const t45 = "flex flex-1 flex-wrap items-center gap-2";
  let t46;
  if ($[65] !== disabled || $[66] !== removeValue) {
    t46 = option => <Tag key={option.value} variant="light" size="md" className="gap-1 pr-1">{option.label}<button type="button" tabIndex={-1} aria-label={`Remove ${option.label}`} disabled={disabled} onClick={e_3 => {
        e_3.stopPropagation();
        removeValue(option.value);
      }} className="flex cursor-pointer items-center justify-center rounded-full text-open-water-500 hover:text-open-water-700"><Icon name="close" className="size-3" decorative={true} /></button></Tag>;
    $[65] = disabled;
    $[66] = removeValue;
    $[67] = t46;
  } else {
    t46 = $[67];
  }
  const t47 = selectedOptions.map(t46);
  let t48;
  if ($[68] !== activeDescendant || $[69] !== descriptionId || $[70] !== disabled || $[71] !== filterable || $[72] !== handleKeyDown || $[73] !== hasError || $[74] !== helperText || $[75] !== id || $[76] !== isEmpty || $[77] !== isOpen || $[78] !== listboxId || $[79] !== open || $[80] !== placeholder || $[81] !== query || $[82] !== setHighlightedIndex || $[83] !== setQuery) {
    t48 = filterable ? <input ref={inputRef} id={id} type="text" role="combobox" autoComplete="off" aria-expanded={isOpen} aria-haspopup="listbox" aria-controls={listboxId} aria-labelledby={`${id}-label`} aria-activedescendant={activeDescendant} aria-autocomplete="list" aria-invalid={hasError || undefined} aria-describedby={hasError || helperText ? descriptionId : undefined} disabled={disabled} value={query} placeholder={isEmpty ? placeholder : undefined} onClick={e_4 => {
      e_4.stopPropagation();
      open();
    }} onChange={e_5 => {
      setQuery(e_5.target.value);
      setHighlightedIndex(0);
      if (!isOpen) {
        open();
      }
    }} onKeyDown={e_6 => handleKeyDown(e_6, true)} className="min-w-24 flex-1 bg-transparent outline-none typography-body-sm-regular text-input-text-rest placeholder:text-input-text-disabled" /> : isEmpty && <span className="text-input-text-disabled">{placeholder}</span>;
    $[68] = activeDescendant;
    $[69] = descriptionId;
    $[70] = disabled;
    $[71] = filterable;
    $[72] = handleKeyDown;
    $[73] = hasError;
    $[74] = helperText;
    $[75] = id;
    $[76] = isEmpty;
    $[77] = isOpen;
    $[78] = listboxId;
    $[79] = open;
    $[80] = placeholder;
    $[81] = query;
    $[82] = setHighlightedIndex;
    $[83] = setQuery;
    $[84] = t48;
  } else {
    t48 = $[84];
  }
  let t49;
  if ($[85] !== t47 || $[86] !== t48) {
    t49 = <div className={t45}>{t47}{t48}</div>;
    $[85] = t47;
    $[86] = t48;
    $[87] = t49;
  } else {
    t49 = $[87];
  }
  let t50;
  if ($[88] !== toggle) {
    t50 = e_7 => {
      e_7.stopPropagation();
      toggle();
    };
    $[88] = toggle;
    $[89] = t50;
  } else {
    t50 = $[89];
  }
  const t51 = disabled ? "text-input-text-disabled" : "text-input-text-rest";
  let t52;
  if ($[90] !== t51) {
    t52 = cn("flex shrink-0 items-center", t51);
    $[90] = t51;
    $[91] = t52;
  } else {
    t52 = $[91];
  }
  let t53;
  if ($[92] !== isOpen) {
    t53 = isOpen ? <ChevronUp className="size-6" /> : <ChevronDown className="size-6" />;
    $[92] = isOpen;
    $[93] = t53;
  } else {
    t53 = $[93];
  }
  let t54;
  if ($[94] !== disabled || $[95] !== t50 || $[96] !== t52 || $[97] !== t53) {
    t54 = <button type="button" tabIndex={-1} aria-hidden={true} disabled={disabled} onClick={t50} className={t52}>{t53}</button>;
    $[94] = disabled;
    $[95] = t50;
    $[96] = t52;
    $[97] = t53;
    $[98] = t54;
  } else {
    t54 = $[98];
  }
  let t55;
  if ($[99] !== t28 || $[100] !== t29 || $[101] !== t30 || $[102] !== t31 || $[103] !== t32 || $[104] !== t33 || $[105] !== t34 || $[106] !== t35 || $[107] !== t36 || $[108] !== t37 || $[109] !== t38 || $[110] !== t39 || $[111] !== t40 || $[112] !== t44 || $[113] !== t49 || $[114] !== t54) {
    t55 = <div id={t28} role={t29} aria-expanded={t30} aria-haspopup={t31} aria-controls={t32} aria-labelledby={t33} aria-activedescendant={t34} aria-invalid={t35} aria-describedby={t36} aria-disabled={t37} tabIndex={t38} onClick={t39} onKeyDown={t40} className={t44}>{t49}{t54}</div>;
    $[99] = t28;
    $[100] = t29;
    $[101] = t30;
    $[102] = t31;
    $[103] = t32;
    $[104] = t33;
    $[105] = t34;
    $[106] = t35;
    $[107] = t36;
    $[108] = t37;
    $[109] = t38;
    $[110] = t39;
    $[111] = t40;
    $[112] = t44;
    $[113] = t49;
    $[114] = t54;
    $[115] = t55;
  } else {
    t55 = $[115];
  }
  let t56;
  if ($[116] !== filteredOptions || $[117] !== highlightedIndex || $[118] !== id || $[119] !== isOpen || $[120] !== listboxId || $[121] !== selectedValues || $[122] !== setHighlightedIndex || $[123] !== toggleOption) {
    t56 = isOpen && <ul ref={listRef} id={listboxId} role="listbox" aria-multiselectable="true" aria-labelledby={`${id}-label`} className="absolute top-[calc(100%+2px)] z-50 max-h-[320px] w-full overflow-y-auto overscroll-contain rounded-lg border border-border-subtle bg-surface-base-primary">{filteredOptions.length === 0 ? <li className="px-3 py-4 typography-body-sm-regular text-input-text-disabled">No matches</li> : filteredOptions.map((option_0, index) => {
        const isSelected = selectedValues.includes(option_0.value);
        return <li key={option_0.value} id={`${id}-option-${index}`} role="option" aria-selected={isSelected} className={cn("flex cursor-pointer items-center justify-between gap-2 px-3 py-4", "typography-body-sm-regular text-text-primary", "hover:bg-input-surface-hover", highlightedIndex === index && "bg-input-surface-hover", isSelected && "font-medium")} onMouseEnter={() => setHighlightedIndex(index)} onMouseDown={e_8 => {
          e_8.preventDefault();
          toggleOption(option_0.value);
        }}><span>{option_0.label}</span>{isSelected && <Icon name="check" className="size-5 shrink-0 text-open-water-500" decorative={true} />}</li>;
      })}</ul>;
    $[116] = filteredOptions;
    $[117] = highlightedIndex;
    $[118] = id;
    $[119] = isOpen;
    $[120] = listboxId;
    $[121] = selectedValues;
    $[122] = setHighlightedIndex;
    $[123] = toggleOption;
    $[124] = t56;
  } else {
    t56 = $[124];
  }
  let t57;
  if ($[125] !== t55 || $[126] !== t56) {
    t57 = <div className={t27}>{t55}{t56}</div>;
    $[125] = t55;
    $[126] = t56;
    $[127] = t57;
  } else {
    t57 = $[127];
  }
  let t58;
  if ($[128] !== descriptionId || $[129] !== errorText || $[130] !== hasError || $[131] !== helperText) {
    t58 = (hasError || helperText) && <p id={descriptionId} role={hasError ? "alert" : undefined} className={cn("typography-component-sm-regular desktop:typography-component-md-regular", hasError ? "text-input-text-error" : "text-input-text-label")}>{errorText || helperText}</p>;
    $[128] = descriptionId;
    $[129] = errorText;
    $[130] = hasError;
    $[131] = helperText;
    $[132] = t58;
  } else {
    t58 = $[132];
  }
  let t59;
  if ($[133] !== containerRef || $[134] !== t20 || $[135] !== t21 || $[136] !== t26 || $[137] !== t57 || $[138] !== t58) {
    t59 = <div ref={containerRef} data-testid={t18} className={t20}>{t21}{t26}{t57}{t58}</div>;
    $[133] = containerRef;
    $[134] = t20;
    $[135] = t21;
    $[136] = t26;
    $[137] = t57;
    $[138] = t58;
    $[139] = t59;
  } else {
    t59 = $[139];
  }
  return t59;
});
MultiSelect.displayName = "MultiSelect";
export { MultiSelect };